import { FileActionButton } from "../file-action/file-action-controls";
import React from "react";
import * as XLSX from "xlsx";
import { Toast } from "primereact/toast";
import { getRawMaterialUtilizationSummaryExport } from "../../services/RawMaterials/RawMaterialService";

interface ExportButtonProps {
  selectedUnitIds: number[];
  filters: {
    fromDate?: Date | null;
    toDate?: Date | null;
    vendorId?: number | null;
    rawMaterialIds?: number[] | null;
    utilizedQtyRange?: [number, number];
    sortBy?: string | null;
    sortDirection?: string | null;
  };
  disabled?: boolean;
}

const RawMaterialHistoryExport: React.FC<ExportButtonProps> = ({
  selectedUnitIds,
  filters,
  disabled = false,
}) => {
  const toast = React.useRef<Toast>(null);

  const parseNumber = (val: any) => {
    if (val == null) return 0;
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const n = parseFloat(val.replace(/[^0-9.\-]/g, ""));
      return isNaN(n) ? 0 : n;
    }
    return 0;
  };

  const rupee = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });

  const formatDateForAPI = (date: Date | null | undefined): string | null => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleExport = async () => {
    if (!selectedUnitIds?.length) {
      toast.current?.show({
        severity: "warn",
        summary: "No Unit Selected",
        detail: "Please select at least one unit before exporting.",
        life: 3000,
      });
      return;
    }

    try {
      const formattedStartDate = formatDateForAPI(filters.fromDate);
      const formattedEndDate = formatDateForAPI(filters.toDate);

      const response = await getRawMaterialUtilizationSummaryExport({
        posIds: selectedUnitIds,
        vendorId: filters.vendorId ?? null,
        rawMaterialIds: filters.rawMaterialIds ?? [],
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        minUtilizedQty: filters.utilizedQtyRange ? filters.utilizedQtyRange[0] : null,
        maxUtilizedQty: filters.utilizedQtyRange ? filters.utilizedQtyRange[1] : null,
        sortBy: filters.sortBy ?? null,
        sortDirection: filters.sortDirection ?? null,
      });

      const data = response?.data || [];

      if (!data.length) {
        toast.current?.show({
          severity: "warn",
          summary: "No Data",
          detail: "No records found for export.",
          life: 3000,
        });
        return;
      }

      // ✅ Prepare data for export with CORRECTED column names
      const exportData = data.map((item) => ({
        "Unit": item.posAddress,
        "Purchase Date": item.purchaseDate
          ? new Date(item.purchaseDate).toLocaleDateString("en-GB")
          : "",
        "Invoice Number": item.invoiceNumber,
        "Vendor": item.vendorName,
        "Raw Material": item.rawMaterialName,
        "Basic Amount": rupee.format(parseNumber(item.basicAmount)),
        "Tax Amount": rupee.format(parseNumber(item.taxAmount)),
        "Total Amount": rupee.format(parseNumber(item.totalAmount)),
        "Invoiced Qty": item.invoicedQuantity,
        "Accepted Qty": item.acceptedQuantity,
        "Rejected Qty": item.rejectedQuantity,
        "QC Remaining Quantity": item.invoicedRemainingQty, // ✅ Changed from "Invoiced Remaining Qty"
        "Accepted Utilized Quantity": item.utilizedQuantityDisplay || "", // ✅ Changed from "QC Utilized Qty"
        "Accepted Unutilized Qty": item.acceptedRemainingQty,
      }));

      // ✅ Generate Excel
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, "Utilization Summary");

      // Auto column widths
      const colWidths = Object.keys(exportData[0]).map((key) => ({
        wch: Math.max(key.length, 15),
      }));
      ws["!cols"] = colWidths;

      const fileName = `RawMaterialUtilizationSummary_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.current?.show({
        severity: "success",
        summary: "Export Successful",
        detail: "Excel file downloaded successfully.",
        life: 3000,
      });
    } catch (error) {
      console.error("Error exporting utilization summary:", error);
      toast.current?.show({
        severity: "error",
        summary: "Export Failed",
        detail: "Something went wrong while exporting.",
        life: 3000,
      });
    }
  };

  return (
    <div className="flex justify-end mb-3">
      <Toast ref={toast} />
      <FileActionButton action="export"
        label="Export Excel"
        icon="pi pi-file-excel"
        className="p-button-success"
        onClick={handleExport}
        disabled={disabled}
        tooltip={disabled ? "Perform a search before exporting." : "Export data to Excel"}
        tooltipOptions={{ position: "left" }}
      />
    </div>
  );
};

export default RawMaterialHistoryExport;
